import type { Message, MessageSender, StoredMessageDto } from '@/types';

/** True when a persisted row belongs in the Bot Consultation pane rather than the customer chat. */
export function isConsultationChannel(channel: string | undefined | null): boolean {
  const ch = (channel ?? '').toLowerCase();
  return ch === 'consultation-bot' || ch === 'consultation-agent';
}

/**
 * Persisted transcript row -> UI Message (splits consultation channels).
 *
 * OP-1041 - a human teammate's reply to the customer is stored on the real `agent` channel as OUTBOUND, and
 * `direction` alone cannot tell it from the bot's, so a reload used to render every teammate's message as
 * the bot's. The staff route now carries `senderKind` / `agentId` (derived by the server from the stored row,
 * never from anything the client sent): `senderKind === 'agent'` is a human, and `agentId` is who. A server
 * that predates the fields sends neither, and then this falls back to the old reading - it never GUESSES an
 * agent.
 *
 * For an AGENT row `senderId` is the agent id (empty when the row did not record one), which is what lets the
 * bubble say "You" only for the signed-in user and name everyone else from the directory.
 */
export function storedMessageToUiMessage(m: StoredMessageDto): Message {
  const ch = (m.channel ?? '').toLowerCase();
  const senderKind = (m.senderKind ?? '').toLowerCase();
  let sender: MessageSender;
  if (ch === 'consultation-bot') sender = 'AI';
  else if (ch === 'consultation-agent') sender = 'AGENT';
  else if (senderKind === 'agent') sender = 'AGENT';
  else sender = m.direction?.toUpperCase() === 'INBOUND' ? 'CUSTOMER' : 'AI';

  const senderId =
    sender === 'AGENT'
      ? (m.agentId ?? '').trim()
      : (m.customerId ?? (sender === 'CUSTOMER' ? 'customer' : 'system'));

  // 2026-10-28 - proposed replies ride ONLY on the bot's consultation note (staff route). Anything else ignores them.
  const proposedReplies = ch === 'consultation-bot' ? normaliseProposedReplies(m.proposedReplies) : undefined;

  return {
    messageId: m.messageId,
    sessionId: m.sessionId,
    sender,
    senderId,
    content: m.content,
    contentType: m.contentType ?? 'text',
    timestamp: m.createdAt,
    attachments: m.attachments,
    stream: isConsultationChannel(m.channel) ? 'consult' : 'chat',
    ...(proposedReplies ? { proposedReplies } : {}),
    ...(typeof m.transactionId === 'string' && m.transactionId.trim() ? { transactionId: m.transactionId.trim() } : {}),
    // 2026-11-01 - the page a CUSTOMER row was sent from (client claim, display only). Never on any other row.
    ...(sender === 'CUSTOMER' && typeof m.pagePath === 'string' && m.pagePath.trim() ? { pagePath: m.pagePath.trim() } : {}),
    ...(sender === 'CUSTOMER' && typeof m.pageTitle === 'string' && m.pageTitle.trim() ? { pageTitle: m.pageTitle.trim() } : {}),
  };
}

/** The server offers at most three; the hub accepts index 0-2. */
export const MAX_PROPOSED_REPLIES = 3;

/**
 * A wire `proposedReplies` value -> what the console holds, or undefined when there is nothing to offer.
 *
 * POSITIONS ARE KEPT. The index the agent sends back is the position in the SERVER's array, so a non-string or blank
 * entry becomes '' in place (the UI skips it) rather than being filtered out, which would shift every later index and
 * make "Send" deliver a different reply than the one on screen. Only the first three positions are kept.
 */
export function normaliseProposedReplies(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out = raw.slice(0, MAX_PROPOSED_REPLIES).map((r) => (typeof r === 'string' ? r : ''));
  return out.some((r) => r.trim().length > 0) ? out : undefined;
}
